/*
 * Copyright (c) Nexign, JSC, 2019-2021.
 */
/**
 *
 * @author Evgenij Tyan
 * @author Yakov Gushchin
 * @author Sergey Mysov
 * @version 1.23.0
 * @since 1.0.0
 */
import React, { ChangeEvent, createRef, MouseEvent, PureComponent, RefObject } from 'react';

import invoke from 'lodash/invoke';

import { Input, LinkBlock } from '../../../../../../components/common';
import { EInputKind } from '../../../../../../components/common/Input/Input';
import { KeyboardKeyCodeEnum } from '../../../../../../modules/app/appConstants';
import withWindowEvents, { IWindowEvent } from '../../../../../../modules/app/hoc/WithWindowEvents';
import { IExtFilterTranslation } from '../../../../../../modules/internationalization/types/pages/subscriberMobile';
import ButtonServiceUserDenied from '../../../../../../modules/role/components/ButtonServiceUserDenied';
import { SaveAsButtonStyled, SaveAsModalContainerStyled } from '../styled/SaveAsButtonStyled';

/**
 * @description Save as button props
 * @interface ISaveAsButtonProps
 * @property {IExtFilterTranslation} translation - locale
 * @property {Function} onCreate - save callback
 */
interface ISaveAsButtonProps {
    onCreate: (type: string, name: string) => void;
    translation: IExtFilterTranslation;
    canSaved: boolean;
}

/**
 * @description Save as button state
 * @interface ISaveAsButtonState
 * @property {boolean} isModalShown - show modal window flag
 * @property {string} name - new filter name
 */
interface ISaveAsButtonState {
    inputKind: EInputKind;
    isModalShown: boolean;
    name: string;
}

export class SaveAsButton extends PureComponent<ISaveAsButtonProps, ISaveAsButtonState> {
    state = {
        inputKind: EInputKind.normal,
        isModalShown: false,
        name: '',
    };

    modalRef: RefObject<HTMLDivElement> = createRef<HTMLDivElement>();

    /**
     * @description Open modal window
     */
    openModal = (): void => {
        this.setState({
            isModalShown: this.props.canSaved,
        });
    };
    /**
     * @description Close modal window
     */
    closeModal = (): void => {
        this.setState({
            isModalShown: false,
        });
    };
    /**
     * @description Check outside click, and close modal window
     * @param {MouseEvent} event
     */
    handleClickOutside = (event: MouseEvent): void => {
        if (this.modalRef.current && !this.modalRef.current.contains(event.target as Node) && this.state.isModalShown) {
            this.closeModal();
        }
    };
    /**
     * @description Change input value handler
     * @param {ChangeEvent<HTMLInputElement>} event - input change event
     */
    inputChangeHandler = (event: ChangeEvent<HTMLInputElement>): void => {
        const name = event.target.value;
        this.setState({
            inputKind: EInputKind.normal,
            name,
        });
    };
    /**
     * @description Create button hanlder
     * if filter name not empty - call onSave and close modal window
     * @param {MouseEvent} event
     */
    handleButtonClick = (event?: MouseEvent): void => {
        invoke(event, 'stopPropagation');
        if (this.state.name) {
            this.setState({
                name: '',
            });
            this.props.onCreate('create', this.state.name);
            this.closeModal();
        } else {
            this.setState({
                inputKind: EInputKind.error,
            });
        }
    };
    keyDownHandler = (event: React.KeyboardEvent) => {
        if (event.keyCode === KeyboardKeyCodeEnum.ENTER) {
            event.stopPropagation();
            this.handleButtonClick();
        }
    };

    render() {
        const { translation } = this.props;
        const { inputKind, isModalShown, name } = this.state;
        return (
            <SaveAsButtonStyled onClick={this.openModal}>
                <LinkBlock disabled={!this.props.canSaved}>{translation.saveAs}</LinkBlock>
                {isModalShown && (
                    <SaveAsModalContainerStyled ref={this.modalRef} onKeyDown={this.keyDownHandler}>
                        <Input
                            autoFocus={true}
                            className="save-as-button__input"
                            kind={inputKind}
                            placeholder={translation.nameFilter}
                            value={name}
                            onChange={this.inputChangeHandler}
                        />
                        <ButtonServiceUserDenied
                            className="save-as-button__save-button"
                            kind="medium"
                            onClick={this.handleButtonClick}
                        >
                            {translation.createFilter}
                        </ButtonServiceUserDenied>
                    </SaveAsModalContainerStyled>
                )}
            </SaveAsButtonStyled>
        );
    }
}

const saveAsEvents: IWindowEvent[] = [
    {
        event: 'click',
        handler: 'handleClickOutside',
    },
];

export default withWindowEvents<ISaveAsButtonProps>(SaveAsButton, saveAsEvents);
